import { cn } from '@/lib/cn';
import {
  For,
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  onCleanup,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** When provided, options are filtered remotely by the caller. */
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  loading?: boolean;
  selectedLabel?: string;
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  class?: string;
}

export const Select = (props: SelectProps): JSX.Element => {
  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [activeIndex, setActiveIndex] = createSignal(-1);
  const [anchor, setAnchor] = createSignal<DOMRect | null>(null);
  let triggerRef: HTMLButtonElement | undefined;
  let panelRef: HTMLDivElement | undefined;
  let searchRef: HTMLInputElement | undefined;
  const baseId = createUniqueId();
  const labelId = `select-label-${baseId}`;
  const listboxId = `select-listbox-${baseId}`;

  const selected = createMemo(() => props.options.find((o) => o.value === props.value));

  const filtered = createMemo(() => {
    const q = query().toLowerCase().trim();
    if (props.onSearch || !q) return props.options;
    return props.options.filter((o) => o.label.toLowerCase().includes(q));
  });

  const openPanel = () => {
    if (props.disabled) return;
    setQuery('');
    props.onSearch?.('');
    setActiveIndex(-1);
    setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    setOpen(true);
    queueMicrotask(() => searchRef?.focus());
  };

  const close = () => {
    setOpen(false);
    triggerRef?.focus();
  };

  const select = (opt: SelectOption) => {
    props.onChange(opt.value);
    close();
  };

  const move = (delta: 1 | -1) => {
    const count = filtered().length;
    if (count === 0) return;
    setActiveIndex((activeIndex() + delta + count) % count);
  };

  const onSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (props.loading) return;
      const opt = filtered()[activeIndex()];
      if (opt) select(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // keep an enclosing Modal's window Escape listener from firing
      close();
    }
  };

  createEffect(() => {
    if (!open()) return;
    const update = () => setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef?.contains(t) || panelRef?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onOutside);
    onCleanup(() => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onOutside);
    });
  });

  const panelStyle = (): JSX.CSSProperties => {
    const rect = anchor();
    if (!rect) return {};
    return {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      'z-index': 60,
    };
  };

  return (
    <div class={cn('flex flex-col gap-1 text-sm', props.class)}>
      <Show when={props.label}>
        <span
          id={labelId}
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            props.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {props.label}
        </span>
      </Show>
      <button
        ref={triggerRef}
        type="button"
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open()}
        aria-controls={listboxId}
        aria-labelledby={props.label ? labelId : undefined}
        onClick={() => (open() ? close() : openPanel())}
        onKeyDown={(e) => {
          if (!open() && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            openPanel();
          }
        }}
        class={cn(
          'relative h-10 w-full rounded-lg border bg-white px-3 pr-8 text-left text-sm text-neutral-900 outline-none transition',
          'border-neutral-300 hover:border-neutral-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:border-neutral-600',
          props.error && 'border-red-500',
        )}
      >
        <span
          class={cn('block truncate', !selected() && !props.selectedLabel && 'text-neutral-500')}
        >
          {selected()?.label ?? props.selectedLabel ?? props.placeholder ?? 'Select…'}
        </span>
        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
          <Icon
            name="angle-small-down"
            class={cn('text-neutral-400 transition-transform', open() && 'rotate-180')}
          />
        </span>
      </button>
      <Show when={open()}>
        <Portal>
          <div
            ref={panelRef}
            style={panelStyle()}
            class="rounded-lg border border-neutral-200 bg-surface shadow-lg dark:border-neutral-800 dark:bg-surface-dark"
          >
            <div class="border-b border-neutral-100 p-2 dark:border-neutral-800">
              <input
                ref={searchRef}
                type="text"
                value={query()}
                onInput={(e) => {
                  setQuery(e.currentTarget.value);
                  props.onSearch?.(e.currentTarget.value);
                  setActiveIndex(-1);
                }}
                onKeyDown={onSearchKeyDown}
                placeholder="Search…"
                aria-controls={listboxId}
                aria-activedescendant={
                  activeIndex() >= 0 ? `${listboxId}-option-${activeIndex()}` : undefined
                }
                class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
              />
            </div>
            <div
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              aria-labelledby={props.label ? labelId : undefined}
              class="max-h-60 overflow-auto py-1"
            >
              <For each={filtered()}>
                {(opt, i) => (
                  <button
                    id={`${listboxId}-option-${i()}`}
                    type="button"
                    role="option"
                    disabled={props.loading}
                    tabIndex={-1}
                    aria-selected={opt.value === props.value}
                    onClick={() => select(opt)}
                    onMouseEnter={() => setActiveIndex(i())}
                    class={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors',
                      opt.value === props.value
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                        : 'text-neutral-700 dark:text-neutral-300',
                      activeIndex() === i() &&
                        opt.value !== props.value &&
                        'bg-neutral-100 dark:bg-neutral-800',
                    )}
                  >
                    {opt.label}
                  </button>
                )}
              </For>
              <Show when={!props.loading && filtered().length === 0}>
                <p class="px-3 py-2 text-sm text-neutral-500">No results</p>
              </Show>
            </div>
            <Show when={props.loading}>
              <p role="status" class="px-3 py-2 text-sm text-neutral-500">
                Loading…
              </p>
            </Show>
            <Show when={props.onLoadMore}>
              <button
                type="button"
                disabled={props.loading}
                onClick={() => props.onLoadMore?.()}
                class="w-full px-3 py-2 text-sm text-brand-600 disabled:opacity-50"
              >
                Load more
              </button>
            </Show>
          </div>
        </Portal>
      </Show>
      <Show when={props.error}>
        <span class="text-xs text-red-600">{props.error}</span>
      </Show>
      <Show when={!props.error && props.hint}>
        <span class="text-xs text-neutral-500">{props.hint}</span>
      </Show>
    </div>
  );
};
